import { supabase } from '@/integrations/supabase/client';
import { CopilotMessage } from '../../types/database-types';
import { mapMessageFromDb, mapMessageToDb, safeJsonToRecord } from '../../utils/dataMappers';
import { logDbOperation, LogCategory, startMeasure, endMeasure, logInfo, logError } from '@/utils/logger';

export interface MessagePage {
  messages: CopilotMessage[];
  hasMore: boolean;
  nextCursor?: string;
  totalCount: number;
}

export interface PaginationOptions {
  limit?: number;
  cursor?: string; // created_at timestamp for cursor-based pagination
  direction?: 'forward' | 'backward'; // forward = newer, backward = older
}

/**
 * Service for CRUD operations on messages
 * Now optimized with PostgreSQL functions for better performance
 */
export class MessageCrudService {
  /**
   * Creates a new message using optimized PostgreSQL function
   */
  static async createMessage(message: Omit<CopilotMessage, 'id' | 'createdAt'>): Promise<CopilotMessage> {
    const startTime = startMeasure('createMessage');
    logDbOperation('RPC', 'ai_agent_create_message', { 
      threadId: message.threadId, 
      role: message.role,
      contentLength: message.content?.length || 0,
      assistantId: message.metadata?.assistant?.id
    });

    const { data, error } = await supabase.rpc('ai_agent_create_message', {
      p_thread_id: message.threadId,
      p_role: message.role,
      p_content: message.content,
      p_assistant_id: message.metadata?.assistant?.id || null,
      p_metadata: message.metadata || null,
      p_tokens_used: message.tokensUsed || null
    });

    if (error) {
      logDbOperation('ERROR', 'ai_agent_create_message', { error: error.message });
      console.error("Error creating message:", error);
      throw new Error(`Failed to create message: ${error.message}`);
    }

    const result = data[0];
    const createdMessage: CopilotMessage = {
      id: result.id,
      threadId: message.threadId,
      role: message.role,
      content: message.content,
      createdAt: result.created_at,
      tokensUsed: message.tokensUsed,
      metadata: message.metadata
    };

    logDbOperation('INSERT_SUCCESS', 'ai_agent_create_message', { messageId: result.id });
    endMeasure(startTime, 'Message creation complete', LogCategory.DATABASE);
    
    return createdMessage;
  }

  /**
   * Fetches recent messages for a thread using optimized PostgreSQL function
   * Loads most recent messages first for immediate display
   */
  static async fetchRecentMessages(threadId: string, limit: number = 20): Promise<CopilotMessage[]> {
    const startTime = startMeasure('fetchRecentMessages');
    logDbOperation('RPC', 'ai_agent_get_messages', { threadId, limit, type: 'recent' });

    const { data, error } = await supabase.rpc('ai_agent_get_messages', {
      p_thread_id: threadId,
      p_limit: limit,
      p_offset: 0
    });

    if (error) {
      logDbOperation('ERROR', 'ai_agent_get_messages', { error: error.message });
      console.error("Error fetching recent messages:", error);
      throw new Error(`Failed to fetch recent messages: ${error.message}`);
    }

    // Convert function results to match existing CopilotMessage format
    const messages = data.map(row => ({
      id: row.id,
      threadId: row.thread_id,
      role: row.role,
      content: row.content,
      createdAt: row.created_at,
      tokensUsed: row.tokens_used,
      metadata: {
        ...safeJsonToRecord(row.metadata),
        assistant: row.assistant_id ? {
          id: row.assistant_id,
          name: row.assistant_name,
          description: '',
          profile_picture_url: row.assistant_profile_picture_url,
          openai_assistant_id: row.assistant_openai_id,
          is_active: true,
          created_at: '',
          updated_at: ''
        } : null
      }
    })).reverse(); // Reverse to get chronological order (oldest first) - preserving existing behavior

    logDbOperation('SELECT_SUCCESS', 'ai_agent_get_messages', { count: messages.length, type: 'recent' });
    logInfo('Recent messages loaded successfully', {
      category: LogCategory.DATABASE,
      data: { 
        threadId, 
        messageCount: messages.length,
        messagesWithAssistants: messages.filter(m => m.metadata?.assistant).length
      }
    });
    endMeasure(startTime, 'Recent messages fetching complete', LogCategory.DATABASE);
    
    return messages;
  }

  /**
   * Fetches messages with pagination support for virtual scrolling
   * PRESERVED: Complex cursor-based pagination logic for compatibility
   */
  static async fetchMessagesPaginated(
    threadId: string, 
    options: PaginationOptions = {}
  ): Promise<MessagePage> {
    const { limit = 20, cursor, direction = 'backward' } = options;
    const startTime = startMeasure('fetchMessagesPaginated');
    
    logDbOperation('SELECT', 'agent_messages', { 
      threadId, 
      limit, 
      direction,
      hasCursor: !!cursor,
      type: 'paginated'
    });

    // Build query based on direction and cursor
    let query = supabase
      .from('agent_messages')
      .select(`
        *,
        agent_assistants (
          id,
          name,
          description,
          profile_picture_url,
          openai_assistant_id,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('thread_id', threadId);

    // Apply cursor-based pagination
    if (cursor) {
      if (direction === 'backward') {
        // Get older messages
        query = query.lt('created_at', cursor);
      } else {
        // Get newer messages
        query = query.gt('created_at', cursor);
      }
    }

    // Apply ordering and limit
    query = query
      .order('created_at', { ascending: direction === 'forward' })
      .limit(limit + 1); // Fetch one extra to check if there are more

    const { data, error } = await query;

    if (error) {
      logDbOperation('ERROR', 'agent_messages', { error: error.message });
      console.error("Error fetching paginated messages:", error);
      throw new Error(`Failed to fetch paginated messages: ${error.message}`);
    }

    // Check if there are more messages
    const hasMore = data.length > limit;
    const messages = hasMore ? data.slice(0, limit) : data;
    
    // Get next cursor (last message's created_at)
    const nextCursor = messages.length > 0 
      ? messages[messages.length - 1].created_at 
      : undefined;

    // Get total count for the thread (cached query)
    const { count: totalCount } = await supabase
      .from('agent_messages')
      .select('id', { count: 'exact' })
      .eq('thread_id', threadId);

    // Map messages
    const mappedMessages = messages.map(dbMessage => {
      const message = mapMessageFromDb(dbMessage, true);
      
      if (dbMessage.agent_assistants) {
        message.metadata = {
          ...message.metadata,
          assistant: {
            id: dbMessage.agent_assistants.id,
            name: dbMessage.agent_assistants.name,
            description: dbMessage.agent_assistants.description,
            profile_picture_url: dbMessage.agent_assistants.profile_picture_url,
            openai_assistant_id: dbMessage.agent_assistants.openai_assistant_id,
            is_active: dbMessage.agent_assistants.is_active,
            created_at: dbMessage.agent_assistants.created_at,
            updated_at: dbMessage.agent_assistants.updated_at
          }
        };
      }
      
      return message;
    });

    // Reverse if getting backward messages to maintain chronological order
    if (direction === 'backward') {
      mappedMessages.reverse();
    }

    const result: MessagePage = {
      messages: mappedMessages,
      hasMore,
      nextCursor,
      totalCount: totalCount || 0
    };

    logDbOperation('SELECT_SUCCESS', 'agent_messages', { 
      count: mappedMessages.length, 
      hasMore,
      totalCount: result.totalCount,
      type: 'paginated'
    });
    
    endMeasure(startTime, 'Paginated messages fetching complete', LogCategory.DATABASE);
    
    return result;
  }

  /**
   * Fetches all messages for a thread using optimized PostgreSQL function
   * PRESERVED: For compatibility with loadAllMessages in useMessageLoading
   */
  static async fetchMessages(threadId: string): Promise<CopilotMessage[]> {
    const startTime = startMeasure('fetchMessages');
    logDbOperation('RPC', 'ai_agent_get_messages', { threadId, type: 'all' });

    const { data, error } = await supabase.rpc('ai_agent_get_messages', {
      p_thread_id: threadId,
      p_limit: 1000, // Large limit to get all messages
      p_offset: 0
    });

    if (error) {
      logDbOperation('ERROR', 'ai_agent_get_messages', { error: error.message });
      console.error("Error fetching messages:", error);
      throw new Error(`Failed to fetch messages: ${error.message}`);
    }

    // Convert function results and maintain ascending order (oldest first)
    const messages = data.map(row => ({
      id: row.id,
      threadId: row.thread_id,
      role: row.role,
      content: row.content,
      createdAt: row.created_at,
      tokensUsed: row.tokens_used,
      metadata: {
        ...safeJsonToRecord(row.metadata),
        assistant: row.assistant_id ? {
          id: row.assistant_id,
          name: row.assistant_name,
          description: '',
          profile_picture_url: row.assistant_profile_picture_url,
          openai_assistant_id: row.assistant_openai_id,
          is_active: true,
          created_at: '',
          updated_at: ''
        } : null
      }
    })); // No reverse - keep ascending order as expected by loadAllMessages

    logDbOperation('SELECT_SUCCESS', 'ai_agent_get_messages', { count: messages.length, type: 'all' });
    logInfo('Messages loaded and transformed successfully', {
      category: LogCategory.DATABASE,
      data: { 
        threadId, 
        messageCount: messages.length,
        messagesWithAssistants: messages.filter(m => m.metadata?.assistant).length
      }
    });
    endMeasure(startTime, 'Messages fetching complete', LogCategory.DATABASE);
    
    return messages;
  }

  /**
   * Fetches messages for a user across all threads (for recent activity, etc.)
   * PRESERVED: Not core to AI agents, keeping existing implementation
   */
  static async fetchUserMessages(userId: string, limit: number = 50): Promise<CopilotMessage[]> {
    const startTime = startMeasure('fetchUserMessages');
    logDbOperation('SELECT', 'agent_messages', { userId, limit });

    const { data, error } = await supabase
      .from('agent_messages')
      .select(`
        *,
        agent_assistants (
          id,
          name,
          description,
          profile_picture_url,
          openai_assistant_id,
          is_active,
          created_at,
          updated_at
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      logDbOperation('ERROR', 'agent_messages', { error: error.message });
      console.error("Error fetching user messages:", error);
      throw new Error(`Failed to fetch user messages: ${error.message}`);
    }

    // Bulk mapping with skipLogging to prevent individual log spam
    const messages = data.map(dbMessage => {
      const message = mapMessageFromDb(dbMessage, true); // Skip individual logging during bulk mapping
      
      // Add assistant information to metadata if available
      if (dbMessage.agent_assistants) {
        message.metadata = {
          ...message.metadata,
          assistant: {
            id: dbMessage.agent_assistants.id,
            name: dbMessage.agent_assistants.name,
            description: dbMessage.agent_assistants.description,
            profile_picture_url: dbMessage.agent_assistants.profile_picture_url,
            openai_assistant_id: dbMessage.agent_assistants.openai_assistant_id,
            is_active: dbMessage.agent_assistants.is_active,
            created_at: dbMessage.agent_assistants.created_at,
            updated_at: dbMessage.agent_assistants.updated_at
          }
        };
      }
      
      return message;
    });

    logDbOperation('SELECT_SUCCESS', 'agent_messages', { count: messages.length });
    endMeasure(startTime, 'User messages fetching complete', LogCategory.DATABASE);
    
    return messages;
  }
}
